import { createFileRoute, Link } from '@tanstack/react-router';
import { useContentType, useContentItems, useDeleteContentItem } from '@wordpuppi/shared';
import { toast } from 'sonner';
import { LoadingSpinner } from '@/components/LoadingSkeleton';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { TypeIcon } from '@/components/TypeIcon';
import { confirmDialog } from '@/lib/dialog';
import { ArrowLeft, Plus, Trash2, Inbox } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

export const Route = createFileRoute('/sites/$siteSlug/content-types/tables/$typeSlug/')({
  component: TableDataPage,
});

function TableDataPage() {
  const { siteSlug, typeSlug } = Route.useParams();
  const { data, isLoading, error } = useContentType(siteSlug, typeSlug);
  const { data: itemsData, isLoading: itemsLoading } = useContentItems(siteSlug, typeSlug);
  const deleteItem = useDeleteContentItem(siteSlug, typeSlug);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  const contentType = data?.data;
  const hasError = error || !contentType || 'error' in contentType;

  if (hasError) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold mb-2">Table not found</h2>
        <p className="text-muted-foreground mb-4">The table "{typeSlug}" doesn't exist.</p>
        <Button asChild>
          <Link to="/sites/$siteSlug/content-types/tables" params={{ siteSlug }}>
            Back to Tables
          </Link>
        </Button>
      </div>
    );
  }

  // Get visible fields (exclude system fields)
  const fields = (contentType.fields || []).filter(
    (f) => !['id', 'site_id', 'created_at', 'updated_at', 'deleted_at', 'author_id'].includes(f.slug)
  );

  const items = itemsData?.data || [];

  const handleDelete = async (id: string) => {
    if (await confirmDialog('Delete this item? This cannot be undone.', { destructive: true, confirmLabel: 'Delete item' })) {
      try {
        await deleteItem.mutateAsync(id);
        toast.success('Item deleted');
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to delete');
      }
    }
  };

  return (
    <div>
      <div className="flex justify-between items-start mb-6">
        <div>
          <Link
            to="/sites/$siteSlug/content-types/tables"
            params={{ siteSlug }}
            className="inline-flex items-center gap-1 text-muted-foreground text-sm hover:text-foreground"
          >
            <ArrowLeft className="size-3.5" />
            Back to Tables
          </Link>
          <h1 className="text-2xl font-semibold mt-2 flex items-center gap-2">
            <TypeIcon icon={contentType.icon} className="size-6 text-muted-foreground" />
            {contentType.name}
          </h1>
        </div>
        <Button asChild>
          <Link
            to="/sites/$siteSlug/content-types/tables/$typeSlug/new"
            params={{ siteSlug, typeSlug }}
          >
            <Plus />
            Add Item
          </Link>
        </Button>
      </div>

      <div className="flex gap-6 p-4 bg-muted/50 border rounded-lg mb-6 text-sm">
        <span>
          <span className="text-muted-foreground">Table:</span>{' '}
          <code className="bg-muted px-1.5 py-0.5 rounded text-foreground">{contentType.slug}</code>
        </span>
        <span>
          <span className="text-muted-foreground">Items:</span> {items.length}
        </span>
        <span>
          <span className="text-muted-foreground">Fields:</span> {fields.length}
        </span>
      </div>

      {itemsLoading ? (
        <LoadingSpinner />
      ) : items.length === 0 ? (
        <div className="text-center py-16 px-6 rounded-xl border-2 border-dashed border-border">
          <div className="mx-auto w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-4">
            <Inbox className="h-6 w-6 text-muted-foreground" />
          </div>
          <p className="text-lg font-medium">Nothing here yet</p>
          <p className="text-muted-foreground mt-1">
            Use "Add Item" above to create the first entry in this table.
          </p>
        </div>
      ) : (
        <div className="rounded-lg border overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                {fields.map((field) => (
                  <TableHead key={field.id}>{field.name}</TableHead>
                ))}
                <TableHead className="w-16 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.id as string}>
                  {fields.map((field) => (
                    <TableCell key={field.id}>
                      <CellValue value={item[field.slug]} fieldType={field.field_type} />
                    </TableCell>
                  ))}
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      className="text-muted-foreground hover:text-destructive"
                      title="Delete item"
                      onClick={() => handleDelete(item.id as string)}
                      disabled={deleteItem.isPending}
                    >
                      <Trash2 />
                      <span className="sr-only">Delete</span>
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

function CellValue({ value, fieldType }: { value: unknown; fieldType: string }) {
  if (value === null || value === undefined || value === '') {
    return <span className="text-muted-foreground/50 italic">—</span>;
  }

  if (fieldType === 'boolean') {
    return (
      <Badge variant={value ? 'success' : 'outline'}>{value ? 'Yes' : 'No'}</Badge>
    );
  }

  if (fieldType === 'json') {
    return (
      <code className="max-w-xs overflow-hidden text-ellipsis whitespace-nowrap block" title={JSON.stringify(value)}>
        {JSON.stringify(value)}
      </code>
    );
  }

  if (fieldType === 'rich_text' || fieldType === 'long_text') {
    const text = String(value);
    return (
      <span className="max-w-xs overflow-hidden text-ellipsis whitespace-nowrap block" title={text}>
        {text.length > 100 ? text.slice(0, 100) + '...' : text}
      </span>
    );
  }

  return <span>{String(value)}</span>;
}
